import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { formatMomentDay } from '@/modules/service-desk/shared/application-utils';
import { latestGeneratedEntry, parseDocumentNumber } from '../../utils/noc-rules';
import { useNocApplication } from '../../hooks/use-noc';
import { downloadNocPdf, NocCopy, NocLetterContent } from '../../utils/noc-pdf';
import { NocLetterPreview } from '../../components/noc-letter-preview/noc-letter-preview';

/** Preview and PDF download of the most recently generated NOC letter of an application. */
export const NocReportPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: application, isLoading } = useNocApplication(id);

  const letter = useMemo<NocLetterContent | null>(() => {
    if (!application) return null;
    const entry = latestGeneratedEntry(application);
    if (!entry) return null;
    // The stored number may carry the letter date as a suffix; otherwise date it the day it was
    // generated, in the application's zone.
    const { number, date } = parseDocumentNumber(
      entry.Data.DocumentNumber,
      formatMomentDay(entry.GeneratedAt, application.Timezone, 'N/A')
    );
    return {
      documentNumber: number,
      letterDate: date,
      applicantName: application.Name,
      body: entry.Data.LetterBody,
      pocName: entry.Data.PocName,
      pocDesignation: entry.Data.PocDesignation,
      pocEmail: entry.Data.PocEmail,
      pocMobile: entry.Data.PocMobile,
    };
  }, [application]);

  const download = (copy: NocCopy) => letter && downloadNocPdf(copy, letter);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => navigate('/hr/service-desk/noc')}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <h1 className="min-w-0 break-words text-lg font-semibold">
            {t('NOC_CERTIFICATE_PREVIEW')}
          </h1>
        </div>
        {letter && (
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Button
              variant="outline"
              className="flex-1 sm:flex-none"
              onClick={() => download('hard')}
            >
              <DownloadSimple className="size-4" />
              {t('DOWNLOAD_HARD_COPY')}
            </Button>
            <Button className="flex-1 sm:flex-none" onClick={() => download('soft')}>
              <DownloadSimple className="size-4" />
              {t('DOWNLOAD_SOFT_COPY')}
            </Button>
          </div>
        )}
      </div>

      {letter ? (
        <NocLetterPreview letter={letter} />
      ) : (
        !isLoading && <p className="text-sm text-muted-foreground">{t('NO_DATA')}</p>
      )}
    </div>
  );
};
